"use client";

import { useEffect, useState } from "react";
import BlogSidebarCTA from "../components/BlogSidebarCTA";

/**
 * Server-rendered Table of Contents for blog posts.
 *
 * Renders crawlable <a href="#section"> jump links from a `headings` prop
 * (generated at build time by scripts/generate-toc-headings.mjs), so the TOC is
 * present in the initial HTML for search engines and AI crawlers — and works with
 * JavaScript disabled. Client JS adds two progressive enhancements: scroll-spy
 * active-section highlighting, and smooth-scroll on click.
 *
 * Layout is CSS-driven (.blog-toc rules in globals.css):
 * - Desktop (>=1025px): sticky left sidebar; optional CTA card renders below.
 * - Mobile (<1025px): collapsible "In this article" toggle above the article.
 *   Links stay in the DOM (CSS-collapsed, not unmounted) so crawlers always see
 *   them — matching the pattern used by ranking competitors.
 *
 * `defaultOpen` renders the mobile list expanded in the server HTML (the user
 * can still collapse it). Only the posts listed in app/blog/posts/toc-pilot.js
 * pass it; see that file for why.
 */
export default function BlogSidebarTOC({
  headings = [],
  sidebarCTA,
  articleSelector = 'article[data-blog-article="true"]',
  defaultOpen = false,
}) {
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(defaultOpen);

  // Scroll-spy: highlight the section currently in view. Reads the rendered
  // article headings (client-only); the link list itself comes from props.
  useEffect(() => {
    const article = document.querySelector(articleSelector);
    if (!article) return;
    const els = Array.from(article.querySelectorAll("h2[id]"));
    if (els.length === 0) return;

    const observers = els.map((h) => {
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(h.id);
        },
        { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
      );
      obs.observe(h);
      return obs;
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [articleSelector]);

  if (!headings || headings.length === 0) return null;

  const handleClick = (e, id) => {
    // Progressive enhancement: smooth-scroll + close the mobile dropdown.
    // If JS is disabled, the native href="#id" still jumps to the section.
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    setOpen(false);
    // Two frames let the mobile dropdown finish collapsing before we measure,
    // so the heading lands at the right scroll position.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        history.replaceState(null, "", `#${id}`);
      });
    });
  };

  return (
    <div
      className="blog-toc"
      data-open={open ? "true" : "false"}
      data-default-open={defaultOpen ? "true" : undefined}
    >
      {/* Mobile-only toggle (hidden on desktop via CSS). */}
      <button
        type="button"
        className="blog-toc-toggle"
        aria-expanded={open}
        aria-controls="blog-toc-nav"
        onClick={() => setOpen((v) => !v)}
      >
        In this article
        <span className="blog-toc-chevron" aria-hidden="true">
          &#9660;
        </span>
      </button>

      <nav id="blog-toc-nav" className="blog-toc-nav" aria-label="Table of contents">
        <div className="blog-toc-eyebrow">In this article</div>
        {headings.map(({ id, text }) => (
          <a
            key={id}
            href={`#${id}`}
            className={`blog-toc-link${activeId === id ? " is-active" : ""}`}
            aria-current={activeId === id ? "true" : undefined}
            onClick={(e) => handleClick(e, id)}
          >
            {text}
          </a>
        ))}
      </nav>

      {sidebarCTA && (
        <div className="blog-toc-cta">
          <BlogSidebarCTA type={sidebarCTA} />
        </div>
      )}
    </div>
  );
}
